import React, { useState, useRef } from 'react';
import { Avatar, Input, Spin } from 'antd';
import { ArrowUp } from '@phosphor-icons/react';
import type { User } from '../types';

const { TextArea } = Input;

export interface SquareCommentInputProps {
  user: User | null;
  onSubmit: (content: string) => Promise<boolean>;
  loading?: boolean;
  placeholder?: string;
}

/**
 * 评论输入组件
 */
export const SquareCommentInput: React.FC<SquareCommentInputProps> = ({
  user,
  onSubmit,
  loading = false,
  placeholder = 'leave a comment here...',
}) => {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const textAreaRef = useRef<any>(null);

  const handleSubmit = async () => {
    if (!content.trim() || submitting || !user) return;

    setSubmitting(true);
    try {
      const success = await onSubmit(content.trim());
      if (success) {
        setContent('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isDisabled = !user || loading || submitting;
  const canSubmit = content.trim().length > 0 && !isDisabled;

  return (
    <div className="square-comment-input">
      <TextArea
        ref={textAreaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={user ? placeholder : '请登录后评论'}
        disabled={isDisabled}
        autoSize={{ minRows: 1, maxRows: 4 }}
        className="square-comment-textarea"
        maxLength={1000}
      />
      <div className="square-comment-input-footer">
        <div className="square-comment-input-user">
          <Avatar
            src={user?.avatarUrl}
            size={24}
            className="square-comment-input-avatar"
          >
            {user?.username?.[0]}
          </Avatar>
          <span className="square-comment-input-username">
            {user?.username || '未登录'}
          </span>
        </div>
        <button
          className={`square-comment-submit-btn ${canSubmit ? 'active' : ''}`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? (
            <Spin size="small" />
          ) : (
            <ArrowUp size={16} weight="bold" />
          )}
        </button>
      </div>
    </div>
  );
};

export default SquareCommentInput;
